import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { productsPage, finishes } from "../mock";
import { Reveal } from "../hooks/useReveal";

function Swatch({ item }) {
  return (
    <Reveal>
      <div className="card-lift group rounded-2xl overflow-hidden bg-[#121317] border border-[#1e2026] hover:border-[var(--af-blue)]/40 h-full">
        <div className="relative h-40 overflow-hidden">
          <div
            className="w-full h-full transition-transform duration-700 group-hover:scale-105"
            style={{
              backgroundColor: item.color,
              backgroundImage: item.light
                ? "none"
                : `repeating-linear-gradient(0deg, rgba(0,0,0,0.16) 0px, rgba(0,0,0,0.16) 1px, transparent 1px, transparent 10px), linear-gradient(120deg, rgba(255,255,255,0.10), rgba(0,0,0,0.10))`,
              boxShadow: item.light ? "inset 0 0 0 1px rgba(0,0,0,0.10)" : "none",
            }}
          />
          <span
            className={`absolute top-4 left-4 text-xs px-3 py-1 rounded-full font-medium ${
              item.light
                ? "bg-[#0c0d0f]/70 text-white"
                : "bg-black/25 text-white backdrop-blur-sm"
            }`}
          >
            uPVC Finish
          </span>
        </div>
        <div className="p-6">
          <h4 className="font-display font-semibold text-2xl text-white mb-2">
            {item.name}
          </h4>
          <p className="text-[var(--af-muted)] leading-relaxed text-sm">
            {item.text}
          </p>
        </div>
      </div>
    </Reveal>
  );
}

export default function Products() {
  const { windowSystems, doorSystems, designedAround } = productsPage;

  return (
    <>
      {/* Header */}
      <section className="relative pt-44 pb-20 bg-[#0a0b0d] border-b border-[#1c1e23] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 70% 20%, var(--af-green), transparent 45%)",
          }}
        />
        <div className="af-container relative z-10">
          <p className="eyebrow mb-5">Products</p>
          <h1 className="font-display font-bold text-white text-5xl md:text-8xl leading-[0.95]">
            uPVC Windows & Doors
          </h1>
          <p className="mt-8 text-lg text-[var(--af-muted)] max-w-3xl leading-relaxed">
            {productsPage.intro}
          </p>
          <p className="mt-4 text-lg text-[var(--af-muted)] max-w-3xl leading-relaxed">
            {productsPage.intro2}
          </p>
        </div>
      </section>

      {/* Window Systems */}
      <section className="py-24 bg-[#0c0d0f]">
        <div className="af-container">
          <Reveal>
            <p className="eyebrow mb-4">01 — Window Systems</p>
            <h2 className="font-display font-bold text-white text-4xl md:text-5xl mb-4">
              {windowSystems.title}
            </h2>
            <p className="text-[var(--af-muted)] max-w-2xl mb-14 text-lg">
              {windowSystems.lead}
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {windowSystems.items.map((item, i) => (
              <Reveal key={item.name} delay={i * 60}>
                <div className="card-lift h-full p-6 rounded-2xl bg-[#121317] border border-[#1e2026] hover:border-[var(--af-blue)]/40">
                  <div className="w-9 h-9 rounded-lg bg-[var(--af-blue)]/12 flex items-center justify-center text-[var(--af-blue)] mb-4">
                    <Check size={17} />
                  </div>
                  <h3 className="font-display font-semibold text-xl text-white mb-2">
                    {item.name}
                  </h3>
                  <p className="text-sm text-[var(--af-muted)] leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Door Systems */}
      <section className="py-24 bg-[#0a0b0d]">
        <div className="af-container">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <Reveal>
              <div>
                <p className="eyebrow mb-4">02 — Door Systems</p>
                <h2 className="font-display font-bold text-white text-4xl md:text-5xl mb-6">
                  {doorSystems.title}
                </h2>
                <p className="text-[var(--af-muted)] text-lg leading-relaxed mb-8">
                  {doorSystems.lead}
                </p>
                <p className="text-[var(--af-muted)] leading-relaxed">
                  These configurations allow architects and homeowners to create
                  openings that are functional, visually balanced, and suited to
                  contemporary living and working environments.
                </p>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="rounded-2xl border border-[#1e2026] bg-[#121317] overflow-hidden">
                {doorSystems.items.map((d, i) => (
                  <div
                    key={d}
                    className="flex items-center gap-4 px-7 py-5 border-b border-[#1c1e23] last:border-0 hover:bg-white/[0.03] transition-colors"
                  >
                    <span className="font-display text-xl text-[var(--af-blue)] w-8">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-white text-lg">{d}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Designed Around Your Space */}
      <section className="py-24 bg-[#0c0d0f]">
        <div className="af-container">
          <Reveal>
            <div className="max-w-3xl">
              <p className="eyebrow mb-4">03 — Flexibility</p>
              <h2 className="font-display font-bold text-white text-4xl md:text-5xl mb-8">
                {designedAround.title}
              </h2>
              {designedAround.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-lg text-[var(--af-muted)] leading-relaxed mb-5"
                >
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Finishes */}
      <section className="py-24 bg-[#0a0b0d] border-t border-[#1c1e23]">
        <div className="af-container">
          <Reveal>
            <p className="eyebrow mb-4">04 — Finishes & Colours</p>
            <h2 className="font-display font-bold text-white text-4xl md:text-6xl mb-6">
              A Palette Built for Architecture
            </h2>
            <p className="text-lg text-[var(--af-muted)] max-w-3xl leading-relaxed mb-16">
              {finishes.intro}
            </p>
          </Reveal>

          {/* Wood-inspired */}
          <Reveal>
            <h3 className="font-display font-semibold text-3xl text-white mb-3">
              {finishes.woodTitle}
            </h3>
            <p className="text-[var(--af-muted)] max-w-2xl mb-10">
              {finishes.woodLead}
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
            {finishes.wood.map((item) => (
              <Swatch key={item.name} item={item} />
            ))}
          </div>

          {/* Solid */}
          <Reveal>
            <h3 className="font-display font-semibold text-3xl text-white mb-10">
              {finishes.solidTitle}
            </h3>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {finishes.solid.map((item) => (
              <Swatch key={item.name} item={item} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0c0d0f]">
        <div className="af-container">
          <Reveal>
            <div className="rounded-3xl border border-[#1e2026] p-12 md:p-16 bg-gradient-to-br from-[#14161b] to-[#0e0f13] text-center">
              <h2 className="font-display font-bold text-white text-4xl md:text-6xl mb-6">
                Design a system around your space.
              </h2>
              <p className="text-lg text-white/70 max-w-xl mx-auto mb-10">
                Tell us about your project and we’ll recommend the right
                configuration, finish and glazing.
              </p>
              <Link
                to="/contact"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0c0d0f] font-semibold rounded-full hover:bg-[var(--af-blue)] hover:text-white transition-all"
              >
                Get a Free Quote
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
